import * as React from 'react';
import classNames from 'classnames';
import { StyleDTO } from 'core/api';
import { Colors } from 'core/ui';
import * as Images from '../Images';

type HasStyle = { Style: StyleDTO };

type GridProps<T> = {
	id?: string;
	rows: 1 | 2 | 3 | 'fill';
	values: T[];
	selectedValue?: Nullable<T>;
	warningValues?: T[];
	buttonChildrenFactory?: ValueButtonChildrenFactory<T>;
	getComparisonKey?: (value: T) => any;
	onValueSelected: (value: T) => void;
	className?: string;
	shouldEnableButton?: (value: T) => boolean;
	style?: React.CSSProperties;
};

export type ValueButtonChildrenFactory<T> = (args: { value: T }) => React.ReactNode;

function areEqual(value: any, selected: any | null | undefined, getComparisonKey?: (x: any) => any): boolean {
	if (value === selected)
		return true;

	if (selected == null)
		return false;

	if (getComparisonKey)
		return getComparisonKey(value) === getComparisonKey(selected);

	if (value.Id !== undefined)
		return value.Id === selected.Id;
	if (value.id !== undefined)
		return value.id === selected.id;

	return false;
}

function getKey(id: string, index: number, value: any): string {
	if (value == null)
		return `${id}_${index}`;

	if (value.Id !== undefined)
		return `${id}_${index}_${value.Id}`;

	if (value.id !== undefined)
		return `${id}_${index}_${value.id}`;

	return `${id}_${index}`;
}

export class ValueButtonGrid<T extends HasStyle> extends React.Component<GridProps<T>, EmptyState> {

	private node = React.createRef<HTMLDivElement>();

	componentDidMount() {
		if (this.node.current === null)
			return;

		const isOverflowing = this.node.current.clientWidth < this.node.current.scrollWidth;
		if (isOverflowing)
			this.node.current.classList.add('has-horizontal-scroll');
	}

	render() {
		const { id, rows, values, selectedValue, warningValues, buttonChildrenFactory, onValueSelected, className, shouldEnableButton, style } = this.props;

		const rowClasses = rows === 'fill'
			? 'rows-fill'
			: 'rows-' + rows;

		const buttons = values.map((value, idx) => <ValueButton
			key={getKey(id || '', idx, value)}
			selected={areEqual(value, selectedValue, this.props.getComparisonKey)}
			hasWarning={warningValues?.some(w => areEqual(value, w, this.props.getComparisonKey))}
			value={value}
			onValueSelected={onValueSelected}
			enabled={!shouldEnableButton || shouldEnableButton(value)}>
			{buttonChildrenFactory ? buttonChildrenFactory({ value }) : undefined}
		</ValueButton>);

		return (
			<div ref={this.node} id={id} className={`btn-value-grid ${rowClasses} ${className || ''}`} style={style}>
				{buttons}
			</div>
		);
	}

}

type ButtonProps<T> = {
	value: T;
	selected: boolean;
	hasWarning?: boolean;
	enabled: boolean;
	onValueSelected: (value: T) => void;
	children?: React.ReactNode;
};

function ValueButton<T extends HasStyle>({ value, selected, onValueSelected, enabled, children, hasWarning }: ButtonProps<T>) {
	const classes = classNames({ 'btn btn-default btn-value': true, 'selected': selected, 'warning': hasWarning });

	const backgroundColor = Colors.getBackgroundColorAsRgb(value.Style.BackColor);
	// A veces no tengo backgroundColor (como en el caso de los botones de los mapas que creamos al vuelo)
	// y quiero dejar la configuración del botón por defecto
	const foregroundColor = backgroundColor ? Colors.getTextColor(backgroundColor) : '';

	const hasImage = !(value.Style.ImageId === Images.NULL_IMAGE || value.Style.ImageId === '');

	const style = hasImage
		? {}
		: { color: foregroundColor, backgroundColor: backgroundColor };


	const inner = React.Children.count(children) ? children : <>
		<img src={Images.imgSrcFromId(value.Style.ImageId)} style={{ display: hasImage ? 'inline' : 'none' }}></img>
		<span>{value.Style.Text}</span>
	</>;

	return (
		<button tabIndex={-1}
			className={classes}
			onClick={_ => onValueSelected(value)}
			style={style}
			disabled={!enabled}>{inner}</button>
	);
}
